import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { filter, map, of, race, switchMap, take, timer } from 'rxjs';
import { EntitlementLevelEnum, FeatureMappingEnum } from '../types/entitlements.types';
import { EntitlementsService } from './entitlements.service';

/**
 * Guard to validate routing according to user entitlements
 * @param route route to activate
 * @returns if can activate
 */
export const entitlementsGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
    const router = inject(Router);
    const entitlementsService = inject(EntitlementsService);

    // Entitlement requirements passed in the route
    const required = route.data['requiredEntitlement'] as {
        entitlement: FeatureMappingEnum;
        level: EntitlementLevelEnum;
    };

    // Wait entitlement information to be ready
    return entitlementsService.ready$.pipe(
        take(1),
        switchMap(ready => {
            if (ready) {
                return of(checkEntitlement());
            } else {
                // If the entitlements are not loaded, waits for the it OR 2 seconds, what comes first
                return race([
                    entitlementsService.ready$.pipe(
                        filter(r => r),
                        take(1),
                        map(() => true)
                    ),
                    timer(2000).pipe(map(() => false))
                ]).pipe(
                    map(entitlementsReady => {
                        return entitlementsReady ? checkEntitlement() : (router.navigate(['']), false);
                    })
                );
            }
        })
    );

    // Check entitlements by level
    function checkEntitlement(): boolean {
        const allowed = entitlementsService.hasEntitlement(required.entitlement, required.level ?? EntitlementLevelEnum.FEATURE);
        if (!allowed) {
            router.navigate(['']);
        }
        return allowed;
    }
};
